/* eslint-disable no-plusplus */
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as dates from "@simplybenefits/sb-dates";
import { TextInput, NumberInput, DateInput, Services, Button, Text, i18n, tokens, localizationMaps } from '@simplybenefits/sb-ui-library';
import { globalStyles } from '../../../theme/index.js';
import { Add as AddIcon, RemoveCircle as RemoveIcon } from '@mui/icons-material';
import {
    addBeneficiaryToList,
    editBeneficiaryInList,
    removeBeneficiaryFromList
} from '../../../actions/signup';
import Tip from '../../../components/Tip.js';

class Beneficiaries extends PureComponent {
    state = {
        beneficiaries: [ ...this.props.beneficiaries ] || [],
        contingentBeneficiaries: [ ...this.props.contingentBeneficiaries ] || [],
        options: Services.selectFieldOptions.optionsWithSpouse(),
        numberOfBeneficiaries: 0,
        numberOfContingentBeneficiaries: 0,
        hasNoBeneficiaries: true
    };

    componentDidMount() {
        this.setState({
            numberOfBeneficiaries: this.state.beneficiaries.length,
            numberOfContingentBeneficiaries: this.state.contingentBeneficiaries.length,
            hasNoBeneficiaries: this.state.beneficiaries.length === 0
        });
    }

    _onBlur = async (type, i, isContingent = false) => {
        // update state with new form field value so it sticks when switching between views
        this.props.dispatch(editBeneficiaryInList(
            i,
            isContingent ? this.state.contingentBeneficiaries[i] : this.state.beneficiaries[i],
            isContingent
        ));

        if (type === 'birthdate') {
            // Show a helper text with a date in english to help the user understand what date they entered
            const birthdateValue = isContingent ? this.state.contingentBeneficiaries[i].birthdate : this.state.beneficiaries[i].birthdate;
            const birthdateError = await Services.validateInput('birthdate', birthdateValue, i18n.locale);
            const birthdateErrorKey = isContingent ? `contingentBirthdateError${ i }` : `birthdateError${ i }`;
            const birthdateHelperKey = isContingent ? `contingentBirthdateHelper${ i }` : `birthdateHelper${ i }`;
            const readableBirthdate = birthdateError ? '' : dates.getStartOfDay(birthdateValue, 'll', i18n.locale);

            this.setState({
                [birthdateErrorKey]: birthdateError,
                [birthdateHelperKey]: readableBirthdate
            });
        }
    };

    _handleInputChange = (type, event, i, isContingent = false) => {
        // make the saved state to false, since we have made edits
        this.props.setSavedState('beneficiaries', false);

        if (i !== null && i !== undefined) {
            if (isContingent) {
                this.setState({
                    contingentBeneficiaries: this.state.contingentBeneficiaries.map((contingent, index) => {
                        if (i === index) {
                            return {
                                ...contingent,
                                [type]: event.target.value
                            };
                        }

                        return contingent;
                    })
                });
            } else {
                this.setState({
                    beneficiaries: this.state.beneficiaries.map((beneficiary, index) => {
                        if (i === index) {
                            return {
                                ...beneficiary,
                                [type]: event.target.value
                            };
                        }

                        return beneficiary;
                    })
                });
            }
        } else {
            this.setState({
                [type]: event.target.value
            });
        }
    };

    _validateForm = async () => {
        let isValid = true;
        let seenSpouse = false;
        let totalBeneficaryShares = 0;

        const promises = this.state.beneficiaries.map(async (beneficiary, i) => {
            const { firstname, lastname, shares, birthdate, relationship, type } = beneficiary;

            const firstnameError = await Services.validateInput('firstname', firstname);
            const lastnameError = await Services.validateInput('lastname', lastname);
            const sharesError = await Services.validateInput('shares', shares);
            const birthdateError = await Services.validateInput('birthdate', birthdate, i18n.locale);
            const relationshipError = await Services.validateInput('relationship', relationship);
            const typeError = await Services.validateInput('checkEmpty', type);
            let spouseError = false;

            if (relationship && localizationMaps.toEnglish("relationships", relationship)?.toUpperCase() === 'SPOUSE') {


                if (seenSpouse) {
                    spouseError = i18n.t(tokens.labels.signup.youCanOnlyHave1Spouse);
                } else {
                    seenSpouse = true;
                }
            }

            this.setState({
                [`firstnameError${ i }`]: firstnameError,
                [`lastnameError${ i }`]: lastnameError,
                [`sharesError${ i }`]: sharesError,
                [`birthdateError${ i }`]: birthdateError,
                [`relationshipError${ i }`]: relationshipError,
                [`typeError${ i }`]: typeError,
                spouseError
            });

            if (
                firstnameError ||
                lastnameError ||
                sharesError ||
                birthdateError ||
                relationshipError ||
                spouseError ||
                typeError
            ) {
                isValid = false;
                return false;
            }

            totalBeneficaryShares += Number(shares);
            return true;
        });

        await Promise.all(promises);

        if (totalBeneficaryShares !== 100) {
            this.setState({
                sharesError: i18n.t(tokens.labels.beneficiaries.allSharesCollectivelyEqual)
            });

            return false;
        }

        // Validate the contingent beneficiaries
        let contingentSeenSpouse = false;
        let totalContingentShares = 0;

        const promises2 = this.state.contingentBeneficiaries.map(async (contingent, i) => {
            const { firstname, lastname, shares, birthdate, relationship } = contingent;

            const firstnameError = await Services.validateInput('firstname', firstname);
            const lastnameError = await Services.validateInput('lastname', lastname);
            const sharesError = await Services.validateInput('shares', shares);
            const birthdateError = await Services.validateInput('birthdate', birthdate, i18n.locale);
            const relationshipError = await Services.validateInput('relationship', relationship);
            let spouseError = false;

            if (relationship && localizationMaps.toEnglish("relationships", relationship).toUpperCase() === 'SPOUSE') {
                if (contingentSeenSpouse) {
                    spouseError = i18n.t(tokens.labels.signup.youCanOnlyHave1Spouse);
                } else {
                    contingentSeenSpouse = true;
                }
            }

            this.setState({
                [`contingentFirstnameError${ i }`]: firstnameError,
                [`contingentLastnameError${ i }`]: lastnameError,
                [`contingentSharesError${ i }`]: sharesError,
                [`contingentBirthdateError${ i }`]: birthdateError,
                [`contingentRelationshipError${ i }`]: relationshipError,
                spouseError
            });

            if (
                firstnameError ||
                lastnameError ||
                sharesError ||
                birthdateError ||
                relationshipError ||
                spouseError
            ) {
                isValid = false;
                return false;
            }

            totalContingentShares += Number(shares);
            return true;
        });

        await Promise.all(promises2);

        if (this.state.contingentBeneficiaries.length > 0 && totalContingentShares !== 100) {
            this.setState({
                contingentSharesError: i18n.t(tokens.labels.beneficiaries.allSharesCollectivelyEqual)
            });

            return false;
        }

        return isValid;
    };

    _createBeneficiaryTemplates = (numberOfBeneficiaries, numberOfContingentBeneficiaries) => {
        const beneficiaries = [];
        const contingentBeneficiaries = [];

        for (let i = 0; i < numberOfBeneficiaries; i++) {
            const beneficiary = {
                firstname: '',
                lastname: '',
                shares: '',
                birthdate: '',
                relationship: '',
                type: 'Revocable'
            };

            beneficiaries.push(beneficiary);
            this.props.dispatch(addBeneficiaryToList(beneficiary, false));
        }

        if (numberOfContingentBeneficiaries && numberOfContingentBeneficiaries > 0) {
            for (let i = 0; i < numberOfContingentBeneficiaries; i++) {
                const contingentBeneficiary = {
                    firstname: '',
                    lastname: '',
                    shares: '',
                    birthdate: '',
                    relationship: '',
                    type: 'Contingent'
                };

                contingentBeneficiaries.push(contingentBeneficiary);
                this.props.dispatch(addBeneficiaryToList(contingentBeneficiary, true));
            }
        }

        this.setState({
            beneficiaries,
            contingentBeneficiaries,
            hasNoBeneficiaries: false
        });
    };

    _next = async () => {
        if (this.state.hasNoBeneficiaries && this.state.numberOfBeneficiaries === 0) {
            this.props.setSavedState('beneficiaries', true);

            this.props.stepCompletion({
                primary: [ ...this.state.beneficiaries ],
                contingent: [ ...this.state.contingentBeneficiaries ]
            }, 'beneficiaries', false);

            return;
        }

        if (this.state.hasNoBeneficiaries && this.state.numberOfBeneficiaries !== 0) {
            await this._createBeneficiaryTemplates(this.state.numberOfBeneficiaries, this.state.numberOfContingentBeneficiaries);
            return;
        }

        const isValid = await this._validateForm();

        if (isValid) {
            // make the saved state to true, we have hit next and everything is valid
            this.props.setSavedState('beneficiaries', true);

            this.props.stepCompletion({
                primary: [ ...this.state.beneficiaries ],
                contingent: [ ...this.state.contingentBeneficiaries ]
            }, 'beneficiaries', false);
        }
    };

    _removeBeneficiary = async (index, isContingent = false) => {
        if (isContingent) {
            await this.setState({
                contingentBeneficiaries: this.state.contingentBeneficiaries.filter((beneficiary, i) => index !== i)
            });
        } else {
            await this.setState({
                beneficiaries: this.state.beneficiaries.filter((beneficiary, i) => index !== i),
                // If we are removing the last remaining primary beneficiary then there can be no contingent beneficiaries
                contingentBeneficiaries: this.state.beneficiaries.length === 1 ? [] : this.state.contingentBeneficiaries
            });
        }

        this.props.dispatch(removeBeneficiaryFromList(index, isContingent));

        if (this.state.beneficiaries.length === 0) {
            this.setState({
                numberOfBeneficiaries: 0,
                hasNoBeneficiaries: true
            });
        } else {
            this.setState({
                numberOfBeneficiaries: this.state.numberOfBeneficiaries - 1
            });
        }
    };

    _addBeneficiary = (isContingent = false) => {
        const beneficiary = {
            firstname: '',
            lastname: '',
            shares: '',
            birthdate: '',
            relationship: '',
            type: i18n.t(tokens.labels.beneficiaries.revocable)
        };

        if (isContingent) {
            beneficiary.type = 'Contingent';

            this.setState({
                contingentBeneficiaries: [ ...this.state.contingentBeneficiaries, beneficiary ]
            });
        } else {
            this.setState({
                beneficiaries: [ ...this.state.beneficiaries, beneficiary ]
            });
        }

        this.props.dispatch(addBeneficiaryToList(beneficiary, isContingent));
    };

    render() {

        const styles = {
            form: {
                display: 'flex',
                flexDirection: 'column'
            },
            stepperBackBtn: {
                margin: '0px auto 0px 0px !important'
            },
            stepperBtnContainer: globalStyles.stepperBtnContainer,
            stepperForm: globalStyles.stepperForm,
            stepperNextBtn: globalStyles.stepperNextBtn
        };

        if (this.state.beneficiaries.length === 0) {
            return (
                <div>
                    <Text largest bold margin="0 0 10px 0">{ i18n.t(tokens.titles.beneficiaries) }</Text>

                    <div style={styles.form}>
                        <Tip
                            left
                            show={ this.props.tips.beneficiaryBreakdown }
                            close={ () => this.props.hideTip('beneficiaryBreakdown') }
                            customStyle={{ marginTop: -100 }}
                        >
                            { i18n.t(tokens.labels.signup.a) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.beneficiary) }</span> { i18n.t(tokens.labels.signup.individualAssignedToReceiveInsuranceAmountsInEvent) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.ofYourDeath) }</span>.
                        </Tip>

                        <TextInput
                            label={ i18n.t(tokens.labels.signup.howManyBeneficiariesDoYouHave) }
                            value={ this.state.numberOfBeneficiaries }
                            onChange={(value) => this._handleInputChange('numberOfBeneficiaries', value)}
                            fullWidth
                        />

                        {
                            this.state.numberOfBeneficiaries > 0 &&
                                <React.Fragment>
                                    <Tip
                                        right
                                        show={ this.props.tips.contingentBeneficiaryBreakdown }
                                        close={ () => this.props.hideTip('contingentBeneficiaryBreakdown') }
                                        customStyle={{ marginTop: 60 }}
                                    >
                                        { i18n.t(tokens.labels.signup.a) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.contingentBeneficiary).toLowerCase() }</span> { i18n.t(tokens.labels.signup.proceedsIfNoPrimaryBeneficiariesAlive) }
                                    </Tip>

                                    <TextInput
                                        label={ i18n.t(tokens.labels.signup.addContingentBeneficiariesOptional) }
                                        value={ this.state.numberOfContingentBeneficiaries }
                                        onChange={(value) => this._handleInputChange('numberOfContingentBeneficiaries', value)}
                                        fullWidth
                                    />
                                </React.Fragment>
                        }

                        <div style={styles.stepperBtnContainer}>
                            <Button
                                style={styles.stepperNextBtn}
                                onClick={this._next}
                                color="primary"
                            >
                                { i18n.t(tokens.buttons.continue) }
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <Text largest bold margin="0 0 10px 0">{ i18n.t(tokens.titles.beneficiaries) }</Text>

                <div style={ styles.form }>
                    <Tip
                        left
                        show={ this.props.tips.beneficiaryBreakdown }
                        close={ () => this.props.hideTip('beneficiaryBreakdown') }
                        customStyle={{ marginTop: -100 }}
                    >
                        { i18n.t(tokens.labels.signup.your) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.beneficiary).toLowerCase() } </span> { i18n.t(tokens.labels.signup.isThePersonsSpouseParentChildThatWill) }&nbsp;
                        <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.receive).toLowerCase() }</span> { i18n.t(tokens.labels.signup.allOfYourGroup) } { i18n.t(tokens.labels.signup.lifeInsuranceBenefits) }{"; "}
                        { i18n.t(tokens.labels.signup.includingCriticalIllnessPayable) }.
                    </Tip>

                    <Tip
                        right
                        show={ this.props.tips.totalShares }
                        close={ () => this.props.hideTip('totalShares') }
                        customStyle={{ marginTop: 160 }}
                    >
                        { i18n.t(tokens.labels.total) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.shareMustEqual100) }</span>.&nbsp;
                        { i18n.t(tokens.labels.signup.moreThanOneBeneficiary) }
                    </Tip>

                    <Tip
                        left
                        show={ this.props.tips.beneficiaryTypeBreakdown }
                        close={ () => this.props.hideTip('beneficiaryTypeBreakdown') }
                        customStyle={{ marginTop: 240 }}
                    >
                        { i18n.t(tokens.labels.signup.a) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.revocableBeneficiary) }</span> { i18n.t(tokens.labels.signup.canBeChangedByYouAtAnyTime) }.&nbsp;
                        { i18n.t(tokens.labels.signup.an) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.irrevocableBeneficiaryCannotBeChanged) }</span>, { i18n.t(tokens.labels.signup.andYou) }&nbsp;
                        { i18n.t(tokens.labels.signup.certainChangesWithoutWrittenConsent) }
                    </Tip>

                    {
                        this.state.beneficiaries.map((beneficiary, i) => (
                            <React.Fragment key={i + 1}>
                                <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', margin: "20px 0 0px 0" }}>
                                    <Text medium bold>{ `${ i18n.t(tokens.labels.signup.beneficiary) } ${ i + 1 }` }</Text>

                                    <Button
                                        onClick={ () => this._removeBeneficiary(i, false) }
                                        transparent
                                        customStyle={{
                                            width: 30,
                                            minWidth: 30,
                                            padding: 0
                                        }}
                                    >
                                        <RemoveIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit' }} />
                                    </Button>
                                </div>

                                <TextInput
                                    label={ i18n.t(tokens.labels.firstname) }
                                    autoComplete="name"
                                    value={ beneficiary.firstname }
                                    onBlur={() => this._onBlur('firstname', i, false)}
                                    onChange={(value) => this._handleInputChange('firstname', value, i, false)}
                                    error={Boolean(this.state[`firstnameError${ i }`])}
                                    helperText={this.state[`firstnameError${ i }`]}
                                    fullWidth
                                />

                                <TextInput
                                    label={ i18n.t(tokens.labels.lastname) }
                                    value={beneficiary.lastname}
                                    onBlur={() => this._onBlur('lastname', i, false)}
                                    onChange={(value) => this._handleInputChange('lastname', value, i, false)}
                                    error={Boolean(this.state[`lastnameError${ i }`])}
                                    helperText={this.state[`lastnameError${ i }`]}
                                    fullWidth
                                />

                                <NumberInput
                                    label={ i18n.t(tokens.labels.signup.shares) }
                                    suffix="%"
                                    value={beneficiary.shares}
                                    fullWidth
                                    onBlur={() => this._onBlur('shares', i, false)}
                                    onChange={(value) => this._handleInputChange('shares', value, i, false)}
                                    error={Boolean(this.state[`sharesError${ i }`]) || Boolean(this.state.sharesError)}
                                    helperText={this.state[`sharesError${ i }`] || this.state.sharesError }
                                />

                                <TextInput
                                    label={ i18n.t(tokens.labels.type) }
                                    value={ beneficiary.type }
                                    fullWidth
                                    onBlur={() => this._onBlur('type', i, false)}
                                    onChange={(value) => this._handleInputChange('type', value, i, false)}
                                    error={Boolean(this.state[`typeError${ i }`]) || Boolean(this.state.typeError)}
                                    helperText={this.state[`typeError${ i }`] || this.state.typeError }
                                    select
                                    selectOptions={ Services.selectFieldOptions.beneficiariesOptions() }
                                />

                                <DateInput
                                    label={ i18n.t(tokens.labels.account.dateOfBirthYYYYMMDD) }
                                    value={beneficiary.birthdate}
                                    onBlur={() => this._onBlur('birthdate', i, false)}
                                    onChange={(value) => this._handleInputChange('birthdate', value, i, false)}
                                    error={Boolean(this.state[`birthdateError${ i }`])}
                                    helperText={this.state[`birthdateError${ i }`] || this.state[`birthdateHelper${ i }`]}
                                    fullWidth
                                />

                                <TextInput
                                    label={ i18n.t(tokens.labels.relationship) }
                                    value={beneficiary.relationship}
                                    fullWidth
                                    onBlur={() => this._onBlur('relationship', i, false)}
                                    onChange={(value) => this._handleInputChange('relationship', value, i, false)}
                                    error={Boolean(this.state[`relationshipError${ i }`]) || Boolean(this.state.spouseError)}
                                    helperText={this.state[`relationshipError${ i }`] || this.state.spouseError }
                                    select
                                    selectOptions={this.state.options}
                                />
                            </React.Fragment>
                        ))
                    }

                    <Button
                        onClick={ () => this._addBeneficiary(false) }
                        transparent
                        customStyle={{
                            marginTop: 2,
                            alignSelf: 'flex-end'
                        }}
                    >
                        <AddIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit', marginRight: 5 }} />
                        { i18n.t(tokens.buttons["add beneficiary"]) }
                    </Button>

                    {
                        this.state.contingentBeneficiaries.length === 0 &&
                            <Button
                                onClick={ () => this._addBeneficiary(true) }
                                transparent
                                customStyle={{
                                    alignSelf: 'flex-end'
                                }}
                            >
                                <AddIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit', marginRight: 5 }} />
                                { i18n.t(tokens.buttons["add contingent beneficiary"]) }
                            </Button>
                    }
                </div>

                <div style={ styles.form }>
                    <Tip
                        right
                        show={ this.props.tips.addAnotherBeneficiary }
                        close={ () => this.props.hideTip('addAnotherBeneficiary') }
                        customStyle={{ marginTop: -100 }}
                    >
                        { i18n.t(tokens.labels.beneficiaries.addBeneficiaryToHealthPlan) }&nbsp;
                        <span style={{ fontWeight: 'bold' }}>"{ i18n.t(tokens.buttons["add beneficiary"].toLowerCase()) }"</span> { i18n.t(tokens.labels.signup.button) }.
                    </Tip>
                </div>

                {
                    this.state.contingentBeneficiaries.length > 0 &&
                        <React.Fragment>
                            <Text largest bold margin="20px 0 10px 0">{ i18n.t(tokens.titles["contingent beneficiaries"])}</Text>

                            <div style={ styles.form }>
                                <Tip
                                    left
                                    show={ this.props.tips.contingentBeneficiaryBreakdown }
                                    close={ () => this.props.hideTip('contingentBeneficiaryBreakdown') }
                                    customStyle={{ marginTop: -80 }}
                                >
                                    { i18n.t(tokens.labels.signup.a) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.contingentBeneficiary).toLowerCase() }</span> { i18n.t(tokens.labels.signup.proceedsIfNoPrimaryBeneficiariesAlive) }.
                                </Tip>

                                {
                                    this.state.contingentBeneficiaries.map((contingent, i) => (
                                        <React.Fragment key={i + 1}>
                                            <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', margin: "20px 0 0px 0" }}>
                                                <Text medium bold>{ `${ i18n.t(tokens.labels.signup.contingentBeneficiary) } ${ i + 1 }` }</Text>

                                                <Button
                                                    onClick={ () => this._removeBeneficiary(i, true) }
                                                    transparent
                                                    customStyle={{
                                                        width: 30,
                                                        minWidth: 30,
                                                        padding: 0
                                                    }}
                                                >
                                                    <RemoveIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit' }} />
                                                </Button>
                                            </div>

                                            <TextInput
                                                label={ i18n.t(tokens.labels.firstname) }
                                                autoComplete="name"
                                                value={ contingent.firstname }
                                                onBlur={() => this._onBlur('firstname', i, true)}
                                                onChange={(value) => this._handleInputChange('firstname', value, i, true)}
                                                error={Boolean(this.state[`contingentFirstnameError${ i }`])}
                                                helperText={this.state[`contingentFirstnameError${ i }`]}
                                                fullWidth
                                            />

                                            <TextInput
                                                label={ i18n.t(tokens.labels.lastname) }
                                                value={contingent.lastname}
                                                onBlur={() => this._onBlur('lastname', i, true)}
                                                onChange={(value) => this._handleInputChange('lastname', value, i, true)}
                                                error={Boolean(this.state[`contingentLastnameError${ i }`])}
                                                helperText={this.state[`contingentLastnameError${ i }`]}
                                                fullWidth
                                            />

                                            <NumberInput
                                                label={ i18n.t(tokens.labels.signup.shares) }
                                                suffix="%"
                                                value={contingent.shares}
                                                fullWidth
                                                onBlur={() => this._onBlur('shares', i, true)}
                                                onChange={(value) => this._handleInputChange('shares', value, i, true)}
                                                error={Boolean(this.state[`contingentSharesError${ i }`]) || Boolean(this.state.contingentSharesError)}
                                                helperText={this.state[`contingentSharesError${ i }`] || this.state.contingentSharesError }
                                            />

                                            <DateInput
                                                label={ i18n.t(tokens.labels.account.dateOfBirthYYYYMMDD) }
                                                value={contingent.birthdate}
                                                onBlur={() => this._onBlur('birthdate', i, true)}
                                                onChange={(value) => this._handleInputChange('birthdate', value, i, true)}
                                                error={Boolean(this.state[`contingentBirthdateError${ i }`])}
                                                helperText={this.state[`contingentBirthdateError${ i }`] || this.state[`contingentBirthdateHelper${ i }`]}
                                                fullWidth
                                            />

                                            <TextInput
                                                label={ i18n.t(tokens.labels.relationship) }
                                                value={contingent.relationship}
                                                fullWidth
                                                onBlur={() => this._onBlur('relationship', i, true)}
                                                onChange={(value) => this._handleInputChange('relationship', value, i, true)}
                                                error={Boolean(this.state[`contingentRlationshipError${ i }`]) || Boolean(this.state.contingentSpouseError)}
                                                helperText={this.state[`contingentRlationshipError${ i }`] || this.state.contingentSpouseError }
                                                select
                                                selectOptions={this.state.options}
                                            />
                                        </React.Fragment>
                                    ))
                                }

                                <Button
                                    onClick={ () => this._addBeneficiary(true) }
                                    transparent
                                    customStyle={{
                                        marginTop: 2,
                                        alignSelf: 'flex-end'
                                    }}
                                >
                                    <AddIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit', marginRight: 5 }} />
                                    { i18n.t(tokens.buttons["add contingent beneficiary"])}
                                </Button>
                            </div>

                            <div style={ styles.form }>
                                <Tip
                                    right
                                    show={ this.props.tips.addAnotherContingentBeneficiary }
                                    close={ () => this.props.hideTip('addAnotherContingentBeneficiary') }
                                    customStyle={{ marginTop: -100 }}
                                >
                                    { i18n.t(tokens.labels.beneficiaries.addContingentBeneficiaryToHealthPlan)}&nbsp;
                                    <span style={{ fontWeight: 'bold' }}>"{ i18n.t(tokens.buttons["add contingent beneficiary"])}"</span> { i18n.t(tokens.labels.signup.button) }.
                                </Tip>
                            </div>
                        </React.Fragment>
                }

                <div style={styles.form}>
                    <div style={styles.stepperBtnContainer}>
                        <Button
                            style={styles.stepperNextBtn}
                            onClick={this._next}
                            color="primary"
                        >
                            { i18n.t(tokens.buttons.continue) }
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

/**
 *
 * @param {*} theme
 */

Beneficiaries.propTypes = {
    beneficiaries: PropTypes.array
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    beneficiaries: state.signup.beneficiaries.primary,
    contingentBeneficiaries: state.signup.beneficiaries.contingent
});

export default (connect(mapStateToProps)(Beneficiaries));